import {
  Alert,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { toggleDownloadOverduePatientModal } from "app/store/nonCompliantPatient/slice/nonCompliantPatientSlice";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import dayjs from "dayjs";
import { useFormik } from "formik";
import React from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useGetManufacturersQuery } from "app/store/manufacturers/api/manufacturersApi";
import * as Yup from "yup";
import { getFormattedDate } from "utils/date";

const DownloadOverduePatientFilterModal = ({
  handleDownloadOverduePatient,
}) => {
  const dispatch = useDispatch();

  const { data: manufacturerList, isLoading: isManufacturersLoading } =
    useGetManufacturersQuery();

  const { showDownloadOverdueModal } = useSelector(
    (state) => state.nonCompliantPatient
  );

  const handleCancel = () => {
    dispatch(toggleDownloadOverduePatientModal(false));
  };

  const validationSchema = Yup.object({
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date cannot be before start date"),
  });

  const formik = useFormik({
    initialValues: {
      startDate: null,
      endDate: null,
      manufacturer: "",
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
    onSubmit: (values) => handleDownloadOverduePatient(values),
  });

  const { values, setFieldValue, touched, errors, handleSubmit, isSubmitting } =
    formik;

  return (
    <ModalWrapper
      show={showDownloadOverdueModal}
      title="Download Overdue Patients"
      showContentDivider
      maxWidth="sm"
      onClose={handleCancel}
      onCancel={handleCancel}
      onConfirm={(e) => handleSubmit(e)}
      actionButtons={[
        {
          label: "Cancel",
          variant: "outlined",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Download",
          onClick: handleSubmit,
          disabled: isSubmitting,
        },
      ]}
    >
      <Alert severity="info" className="mt-2" style={{ color: "#2196F3" }}>
        Select a date range to view overdue patients based on their late dates.
      </Alert>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Start Date"
            format="MM/DD/YYYY"
            name="startDate"
            value={values.startDate ? dayjs(values.startDate) : null}
            minDate={dayjs("2022-01-01")}
            maxDate={values.endDate ? dayjs(values.endDate) : dayjs()}
            onChange={(date) => {
              const startDate = date ? getFormattedDate(date) : null;
              setFieldValue("startDate", startDate);
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: touched.startDate && Boolean(errors?.startDate),
                helperText: touched.startDate && errors.startDate,
                readOnly: true,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="End Date"
            format="MM/DD/YYYY"
            name="endDate"
            value={values.endDate ? dayjs(values.endDate) : null}
            onChange={(date) => {
              const endDate = date ? getFormattedDate(date) : null;
              setFieldValue("endDate", endDate);
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: touched.endDate && Boolean(errors?.endDate),
                helperText: touched.endDate && errors.endDate,
                readOnly: true,
              },
            }}
            maxDate={dayjs()}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="manufacturer-label" style={{ background: "white" }}>
              Select Manufacturer
            </InputLabel>
            <Select
              id="manufacturer"
              value={values.manufacturer}
              name="manufacturer"
              onChange={(e) => setFieldValue("manufacturer", e.target.value)}
              label="Select Manufacturer"
            >
              {isManufacturersLoading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : (
                manufacturerList?.map((manufacturer) => (
                  <MenuItem key={manufacturer.name} value={manufacturer.name}>
                    {manufacturer.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </ModalWrapper>
  );
};

DownloadOverduePatientFilterModal.propTypes = {
  handleDownloadOverduePatient: PropTypes.func.isRequired,
};

export default DownloadOverduePatientFilterModal;
